import { inject, observer } from "mobx-react";
import React, { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, matchPath } from "react-router-dom";

import { ClassExtra, ClassMenu } from "../../config/page_route";

const Content = ({ rootStore }) => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath.startsWith("/class")) {
      rootStore.setCorpMode(false);
    }
  }, [location.pathname]);

  // 타이틀 설정 로직
  const setTitle = (pathname: string, isCorpMode: boolean) => {
    const defaultTitle = "카리페이 관리자";

    // 기관 모드와 클래스 모드에 따라 적절한 메뉴 선택
    const mainMenus = ClassMenu;
    const extraMenus = ClassExtra;

    // 모든 메뉴 아이템을 하나의 배열로 합침
    const allMenuItems = [
      ...extraMenus,
      ...mainMenus.flatMap((menu) => menu.children),
    ];

    // 현재 경로와 일치하는 메뉴 아이템 찾기
    // 더 구체적인 경로가 우선적으로 매칭되도록 정렬
    const sortedMenuItems = [...allMenuItems].sort((a, b) => {
      // 정확한 경로 일치가 우선
      if (pathname === a.path && pathname !== b.path) return -1;
      if (pathname === b.path && pathname !== a.path) return 1;

      // containPath 길이가 긴 것이 우선 (더 구체적인 경로)
      const aContainPathLength = a.containPath
        ? Math.max(...a.containPath.map((p) => p.length))
        : 0;
      const bContainPathLength = b.containPath
        ? Math.max(...b.containPath.map((p) => p.length))
        : 0;

      return bContainPathLength - aContainPathLength;
    });

    const matched = sortedMenuItems.find((menu) => {
      // 정확한 경로 일치 확인
      const matchByPath = 
        !!matchPath({ path: menu.path, end: true }, pathname);

      // containPath 배열이 있는 경우, 해당 경로로 시작하는지 확인
      const matchByContain = menu.containPath?.some((p) =>
        !!matchPath({ path: p, end: false }, pathname)
      );

      return matchByPath || matchByContain;
    });

    // 페이지 타이틀 설정
    const pageTitle = matched?.title || defaultTitle;
    const fullTitle = `${pageTitle}`;
    document.title = fullTitle;
  };

  // 페이지 이동 감지 시 타이틀 설정
  useEffect(() => {
    setTitle(location.pathname, rootStore.getCorpMode);
  }, [location.pathname, rootStore.getCorpMode]);

  // 부모 메뉴 클릭 시 첫 번째 자식 메뉴로 리다이렉트
  const handleParentRoute = (route) => {
    if (route.children && route.children.length > 0) {
      const firstChild = route.children[0];
      return <Navigate to={firstChild.path} replace />;
    }
    return null;
  };

  // 라우트 렌더링
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Class Routes */}
        {ClassMenu.filter(
          (route) =>
            !route.user_type || route.user_type === rootStore.getProfile?.userType
        ).map((route, index) => (
          <React.Fragment key={`class-${index}`}>
            {/* 부모 메뉴 라우트 */}
            <Route
              path={route.path}
              element={
                !route.private || rootStore.isSignedIn ? (
                  handleParentRoute(route)
                ) : (
                  <Navigate to="/login" state={{ from: location }} replace />
                )
              }
            />
            {/* 자식 메뉴 라우트들 */}
            {route.children
              .filter(
                (subRoute) =>
                  !subRoute.user_type ||
                  subRoute.user_type === rootStore.getProfile?.userType
              )
              .map((subRoute, subIndex) => (
                <Route
                  key={`class-${index}-${subIndex}`}
                  path={subRoute.path}
                  element={
                    !subRoute.private || rootStore.isSignedIn ? (
                      subRoute.component()
                    ) : (
                      <Navigate to="/login" state={{ from: location }} replace />
                    )
                  }
                />
              ))}
          </React.Fragment>
        ))}
        {ClassExtra.filter(
          (route) =>
            !route.user_type || route.user_type === rootStore.getProfile?.userType
        ).map((route, index) => (
          <Route
            key={`class-extra-${index}`}
            path={route.path}
            element={
              !route.private || rootStore.isSignedIn ? (
                route.component()
              ) : (
                <Navigate to="/login" state={{ from: location }} replace />
              )
            }
          />
        ))}
        <Route
          path="*"
          element={<Navigate to="/login" state={{ from: location }} replace />}
        />
      </Routes>
    </Suspense>
  );
};

export default inject("rootStore")(observer(Content));
